import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ant design icons
import DisconnectOutlined from '@ant-design/icons/DisconnectOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import SyncOutlined from '@ant-design/icons/SyncOutlined';

// project imports
import MainCard from 'components/MainCard';
import useOnlineStatus from 'hooks/useOnlineStatus';
import useAuth from 'hooks/useAuth';
import { getCachedChildren, getCachedChildrenForTherapist, getUnsyncedCount, getUnsyncedCountForTherapist } from 'utils/offlineDB';

// Shared DataCollection component
import DataCollection from 'pages/therapist/data-collection';

// Offline profile picker
import OfflineProfilePicker from 'pages/offline/OfflineProfilePicker';

// ==============================|| OFFLINE DATA ENTRY PAGE ||============================== //

const OfflineDataEntry = () => {
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isOnline, isOfflineReady, refreshUnsyncedCount, offlineUser, activeOfflineUser } = useOnlineStatus();
  // Authenticated user is the source of truth: a logged-in therapist must never
  // be sent to the profile picker when the connection drops.
  const { user: authUser, isLoggedIn } = useAuth();

  const [cachedChildren, setCachedChildren] = useState([]);
  const [localUnsyncedCount, setLocalUnsyncedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Determine which user to use:
  // 1. activeOfflineUser = selected via profile picker (PIN verified)
  // 2. offlineUser = was logged in when internet dropped (no PIN needed)
  // 3. authUser = currently authenticated user (covers reload-while-logged-in)
  const effectiveUser = activeOfflineUser || offlineUser || authUser;
  const effectivePersonId = activeOfflineUser?.personId || offlineUser?.person_id || offlineUser?.id || authUser?.person_id || authUser?.id;

  // Load cached children on mount or when effective user changes
  useEffect(() => {
    const loadCachedData = async () => {
      try {
        let children;
        if (effectivePersonId) {
          // Strictly therapist-scoped: each profile sees ONLY its own children.
          // No cross-therapist fallback (it would leak another therapist's list).
          children = await getCachedChildrenForTherapist(effectivePersonId);
        } else {
          // No identified user — fall back to all cached children (legacy).
          children = await getCachedChildren();
        }
        setCachedChildren(children);

        const effectiveUserId = activeOfflineUser?.id || offlineUser?.id;
        const count = effectiveUserId ? await getUnsyncedCountForTherapist(effectiveUserId) : await getUnsyncedCount();
        setLocalUnsyncedCount(count);
      } catch (error) {
        console.error('Error loading cached data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCachedData();
    // Reload only when the effective therapist changes; id lookups are derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePersonId]);

  // Refresh unsynced count when a form is saved
  const handleFormSaved = async () => {
    const effectiveUserId = activeOfflineUser?.id || offlineUser?.id;
    const count = effectiveUserId ? await getUnsyncedCountForTherapist(effectiveUserId) : await getUnsyncedCount();
    setLocalUnsyncedCount(count);
    refreshUnsyncedCount();
  };

  // Show the profile picker ONLY when offline AND nobody is authenticated AND no
  // offline session / picked profile exists. A logged-in therapist skips it.
  if (!isOnline && !isLoggedIn && !offlineUser && !activeOfflineUser) {
    return <OfflineProfilePicker />;
  }

  // If loading, show loading state
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>{intl.formatMessage({ id: 'offline.dataEntry.loading' })}</Typography>
      </Box>
    );
  }

  // If not offline ready (user hasn't logged in with internet at least once)
  if (!isOfflineReady && cachedChildren.length === 0) {
    return (
      <MainCard>
        <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
          <DisconnectOutlined style={{ fontSize: '4rem', color: theme.palette.warning.main }} />
          <Typography variant="h4" textAlign="center">
            {intl.formatMessage({ id: 'offline.dataEntry.notAvailableTitle' })}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 500 }}>
            {intl.formatMessage({ id: 'offline.dataEntry.notAvailableDescription' })}
          </Typography>
          {isOnline && (
            <Button variant="contained" onClick={() => navigate('/login')} startIcon={<UserOutlined />}>
              {intl.formatMessage({ id: 'offline.dataEntry.goToLogin' })}
            </Button>
          )}
        </Stack>
      </MainCard>
    );
  }

  // If no children cached
  if (cachedChildren.length === 0) {
    return (
      <MainCard>
        <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
          <UserOutlined style={{ fontSize: '4rem', color: theme.palette.grey[400] }} />
          <Typography variant="h4" textAlign="center">
            {intl.formatMessage({ id: 'offline.dataEntry.noChildren' })}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 500 }}>
            {intl.formatMessage({ id: 'offline.dataEntry.noChildrenDescription' })}
          </Typography>
          {isOnline && (
            <Button variant="contained" onClick={() => navigate('/login')} startIcon={<SyncOutlined />}>
              {intl.formatMessage({ id: 'offline.dataEntry.loginSyncData' })}
            </Button>
          )}
        </Stack>
      </MainCard>
    );
  }

  // Render the shared DataCollection component with offline props
  return (
    <DataCollection
      isOfflineMode={true}
      offlineChildren={cachedChildren}
      offlineUser={effectiveUser}
      isOnline={isOnline}
      unsyncedCount={localUnsyncedCount}
      onFormSaved={handleFormSaved}
    />
  );
};

export default OfflineDataEntry;
